import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2020 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Avatar, IconButton, Link, Menu, MenuItem, Typography } from '@mui/material';
import { useLocation, Link as RouteLink } from 'react-router';
import { UserAvatar } from '../pages/user/avatar';
import { UserSettingsRoutes } from '../pages/user/user-settings-routes';
import { PublishRoutes } from '../pages/publish/publish-routes';
import { styled } from '@mui/material/styles';
import { MainContext } from '../context';
import { KbdKey } from '../components/kbd-key';
import { PublishButton } from '../components/publish/publish-button';
import { useShortcut } from '../hooks/use-shortcut';
import { focusOutline } from '../components/page-primitives';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GitHubIcon from '@mui/icons-material/GitHub';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PublishIcon from '@mui/icons-material/Publish';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InfoIcon from '@mui/icons-material/Info';
import { AdminDashboardRoutes } from '../pages/admin-dashboard/admin-dashboard-routes';
import { hasAnyAdminAccess } from '../permissions';
import { LogoutForm } from '../pages/user/logout';
import { LoginComponent } from './login';
// Shared destinations so a menu item's link, its keyboard shortcut, and its hint
// can't drift apart. External docs open in the same tab, matching the link click.
const DOCS_URL = 'https://github.com/eclipse/openvsx/wiki';
//-------------------- Mobile View --------------------//
// eslint-disable-next-line react-refresh/only-export-components
export const itemIcon = {
    mr: 1,
    width: '16px',
    height: '16px'
};
export const MenuItemText = ({ children }) => {
    return (_jsx(Typography, { variant: 'body2', color: 'text.primary', sx: { display: 'flex', alignItems: 'center', textTransform: 'none' }, children: children }));
};
export const MobileUserAvatar = () => {
    const { user, pageSettings } = useContext(MainContext);
    const { userMenuContent: UserMenuContent } = pageSettings.elements;
    const logoutFormRef = useRef(null);
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    // Stable identity: a new component type each render would remount contributed entries,
    // refetching whatever they load.
    const MenuEntry = useMemo(() => {
        const Entry = ({ to, icon: Icon, children }) => (_jsx(MenuItem, { component: RouteLink, to: to, onClick: close, children: _jsxs(MenuItemText, { children: [_jsx(Icon, { sx: itemIcon }), children] }) }));
        Entry.displayName = 'MobileUserMenuEntry';
        return Entry;
    }, [close]);
    if (!user) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs(MenuItem, { ref: anchorRef, onClick: () => setOpen(true), "aria-haspopup": 'menu', "aria-expanded": open, children: [_jsxs(MenuItemText, { children: [_jsx(Avatar, { src: user.avatarUrl, alt: user.loginName, variant: 'rounded', sx: itemIcon }), user.loginName] }), _jsx(MoreVertIcon, { fontSize: 'small', sx: { ml: 'auto', color: 'text.secondary' } })] }), _jsxs(Menu, { open: open, onClose: close, anchorEl: anchorRef.current, anchorOrigin: { vertical: 'top', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, children: [_jsx(MenuItem, { component: Link, href: user.homepage, onClick: close, children: _jsxs(MenuItemText, { children: [_jsx(GitHubIcon, { sx: itemIcon }), user.loginName] }) }), _jsx(MenuItem, { component: RouteLink, to: UserSettingsRoutes.PROFILE, onClick: close, children: _jsxs(MenuItemText, { children: [_jsx(SettingsIcon, { sx: itemIcon }), "Settings"] }) }), UserMenuContent ? _jsx(UserMenuContent, { close: close, MenuEntry: MenuEntry }) : null, hasAnyAdminAccess(user) ? (_jsx(MenuItem, { component: RouteLink, to: AdminDashboardRoutes.MAIN, onClick: close, children: _jsxs(MenuItemText, { children: [_jsx(AdminPanelSettingsIcon, { sx: itemIcon }), "Admin Dashboard"] }) })) : null, _jsx(MenuItem, { onClick: () => logoutFormRef.current?.submit(), children: _jsx(LogoutForm, { ref: logoutFormRef, children: _jsxs(MenuItemText, { children: [_jsx(LogoutIcon, { sx: itemIcon }), "Log Out"] }) }) })] })] }));
};
export const MobileMenuContent = () => {
    const location = useLocation();
    const { user, loginProviders } = useContext(MainContext);
    return (_jsxs(_Fragment, { children: [loginProviders &&
                (user ? (_jsx(MobileUserAvatar, {})) : (_jsx(LoginComponent, { loginProviders: loginProviders, renderButton: (href, onClick) => (_jsx(MenuItem, { component: Link, href: href, onClick: onClick, children: _jsxs(MenuItemText, { children: [_jsx(AccountBoxIcon, { sx: itemIcon }), "Log In"] }) })) }))), loginProviders && !location.pathname.startsWith(PublishRoutes.ROOT) && (_jsx(MenuItem, { component: RouteLink, to: PublishRoutes.ROOT, children: _jsxs(MenuItemText, { children: [_jsx(PublishIcon, { sx: itemIcon }), "Publish Extension"] }) })), _jsx(MenuItem, { component: Link, href: 'https://github.com/eclipse/openvsx', target: '_blank', children: _jsxs(MenuItemText, { children: [_jsx(GitHubIcon, { sx: itemIcon }), "Source Code"] }) }), _jsx(MenuItem, { component: Link, href: DOCS_URL, children: _jsxs(MenuItemText, { children: [_jsx(MenuBookIcon, { sx: itemIcon }), "Documentation"] }) }), _jsx(MenuItem, { component: RouteLink, to: '/about', children: _jsxs(MenuItemText, { children: [_jsx(InfoIcon, { sx: itemIcon }), "About This Service"] }) })] }));
};
//-------------------- Default View --------------------//
// eslint-disable-next-line react-refresh/only-export-components
export const headerItem = ({ theme }) => ({
    margin: theme.spacing(0, 0.5),
    padding: theme.spacing(1, 1.5),
    // Inherited color so the items follow the nav's content color over page
    // bands; opacity stands in for the secondary/primary text pair.
    color: 'inherit',
    opacity: 0.78,
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    letterSpacing: 0,
    borderRadius: `${theme.shape.borderRadius}px`,
    transition: 'background 0.14s, opacity 0.14s',
    '&:hover': {
        opacity: 1,
        backgroundColor: 'color-mix(in srgb, currentcolor 8%, transparent)',
        textDecoration: 'none'
    },
    ...focusOutline(theme)
});
// eslint-disable-next-line react-refresh/only-export-components
export const MenuLink = styled(Link)(headerItem);
// eslint-disable-next-line react-refresh/only-export-components
export const MenuRouteLink = styled(RouteLink)(headerItem);
export const DefaultMenuContent = () => {
    const { user, loginProviders } = useContext(MainContext);
    // Register each shortcut next to the control it drives, sharing the same
    // destination so the hint, the click, and the keypress stay in sync. Publish
    // registers its own, next to the keycap on PublishButton.
    useShortcut({ key: 'd', label: 'Documentation', order: 2, callback: () => window.location.assign(DOCS_URL) });
    return (_jsxs(_Fragment, { children: [_jsxs(MenuLink, { href: DOCS_URL, sx: { display: 'inline-flex', alignItems: 'center', gap: '0.4375rem' }, children: ["Documentation", _jsx(KbdKey, { children: "d" })] }), _jsx(MenuRouteLink, { to: '/about', children: "About" }), loginProviders && (_jsxs(_Fragment, { children: [_jsx(PublishButton, {}), user ? (_jsx(UserAvatar, {})) : (_jsx(LoginComponent, { loginProviders: loginProviders, renderButton: (href, onClick) => {
                            // Inherited color + opacity (not a palette gray) so the dimming
                            // follows the nav's content color over tinted chromes, like headerItem.
                            const loginButtonSx = {
                                opacity: 0.78,
                                transition: 'opacity 0.14s',
                                '&:hover': { opacity: 1 }
                            };
                            if (href) {
                                return (_jsx(IconButton, { href: href, color: 'inherit', title: 'Log In', "aria-label": 'Log In', sx: loginButtonSx, children: _jsx(AccountBoxIcon, { sx: { fontSize: '1.25rem' } }) }));
                            }
                            else {
                                return (_jsx(IconButton, { onClick: onClick, color: 'inherit', title: 'Log In', "aria-label": 'Log In', sx: loginButtonSx, children: _jsx(AccountBoxIcon, { sx: { fontSize: '1.25rem' } }) }));
                            }
                        } }))] }))] }));
};
//# sourceMappingURL=menu-content.js.map